import { Link } from "react-router-dom";
import { ExternalLink, Link2, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Kicker } from "@/components/ui/kicker";
import { PersonaVoice } from "@/components/ui/persona-voice";
import { MarkdownProse } from "@/components/ui/markdown-prose";
import { FindingPolarityMark } from "@/components/experience/finding-polarity-mark";
import { FindingObservation } from "@/components/experience/finding-observation";
import {
  FindingFixDropdown,
  FindingTriageMenu,
} from "@/components/experience/finding-actions";
import { ProviderIcon } from "@/components/workflow/workflow-rail";
import { trpc } from "@/lib/trpc";
import { isProblemType } from "@iamu/shared";
import type { ZenCarouselSidebarProps } from "./zen-types";

export function ZenCarouselSidebar({
  finding,
  projectId,
  canEdit,
  hasLinkedRepo = false,
  isTrackerConfigured,
  activeProvider,
  createIssuePending,
  onCreateIssue,
  onLinkExisting,
}: ZenCarouselSidebarProps) {
  const isProblem = isProblemType(finding.type);
  const displayTitle = finding.title || finding.description;
  const canonicalOrRawId = finding.canonicalFindingId ?? finding.id;

  const { data: detail } = trpc.finding.getById.useQuery(
    { id: canonicalOrRawId },
    {
      enabled: Boolean(
        !finding.uiFact &&
          !finding.consequence &&
          !finding.what &&
          canonicalOrRawId,
      ),
    },
  );

  const rawDetail = detail as
    | {
        effectiveUiFact?: string | null;
        effectiveConsequence?: string | null;
        effectiveWhat?: string | null;
        effectiveWhy?: string | null;
        effectiveBasis?: string | null;
        effectiveRecommendation?: string | null;
        observations?: Array<{
          stepNumber?: number | null;
          stepTitle?: string | null;
          personaReasoning?: string | null;
        }>;
      }
    | undefined;

  const matchedObs =
    rawDetail?.observations?.find(
      (o) =>
        typeof finding.stepNumber === "number" &&
        o.stepNumber === finding.stepNumber,
    ) ?? rawDetail?.observations?.[0];

  const uiFact = finding.uiFact ?? rawDetail?.effectiveUiFact ?? null;
  const consequence =
    finding.consequence ?? rawDetail?.effectiveConsequence ?? null;
  const what = finding.what ?? rawDetail?.effectiveWhat ?? null;
  const why = finding.why ?? rawDetail?.effectiveWhy ?? null;
  const basis = finding.basis ?? rawDetail?.effectiveBasis ?? null;
  const recommendation =
    finding.recommendation ?? rawDetail?.effectiveRecommendation ?? null;
  const personaQuote =
    finding.personaQuote ?? matchedObs?.personaReasoning ?? null;
  const stepTitle = finding.stepTitle ?? matchedObs?.stepTitle ?? null;

  const effectiveState =
    finding.triageStatus === "addressed"
      ? "fix_applied"
      : finding.triageStatus === "deferred"
        ? "snoozed"
        : finding.triageStatus === "wont_fix"
          ? "risk_accepted"
          : (finding.triageStatus ?? "open");

  return (
    <div className="flex h-full w-full flex-col justify-between overflow-y-auto border-l border-border/60 bg-card p-6">
      <div className="space-y-6">
        {/* Header & Meta */}
        <header className="space-y-2 border-b border-border/40 pb-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <FindingPolarityMark
                type={finding.type}
                severity={finding.severity}
              />
              <Kicker>
                {typeof finding.stepNumber === "number"
                  ? `Step ${finding.stepNumber}`
                  : "Walkthrough finding"}
              </Kicker>
            </div>
            {finding.severity && (
              <Badge variant="outline" className="text-2xs capitalize">
                {finding.severity}
              </Badge>
            )}
          </div>

          <h2 className="text-lg font-semibold text-foreground leading-snug">
            {displayTitle}
          </h2>

          {stepTitle && (
            <p className="text-xs text-muted-foreground">During: {stepTitle}</p>
          )}
        </header>

        {/* Persona Voice / Thoughts */}
        {personaQuote && (
          <section className="space-y-1.5" aria-label="Persona thought">
            <Kicker>
              {finding.personaName
                ? `${finding.personaName}’s reaction`
                : "Persona thought"}
            </Kicker>
            <div className="rounded-md border border-border/40 bg-muted/20 p-3.5">
              <PersonaVoice size="base" className="leading-relaxed">
                “
                {personaQuote
                  .replace(/^[A-Za-zÀ-ÖØ-öø-ÿ]+\s*:\s*/, "")
                  .replace(/^["“”]+|["“”]+$/g, "")}
                ”
              </PersonaVoice>
            </div>
          </section>
        )}

        {/* Structured Observation */}
        <section className="space-y-2" aria-label="Observation evidence">
          <FindingObservation
            uiFact={uiFact}
            consequence={consequence}
            what={what}
            why={why}
            basis={basis}
            screen={finding.screen}
            breaksRule={finding.breaksRule}
            breaksFile={finding.breaksFile}
            description={finding.description}
            designCauses={finding.designCauses}
          />
        </section>

        {/* Recommendation */}
        {recommendation && (
          <section
            className="space-y-1.5 border-t border-border/40 pt-4"
            aria-label="Recommendation"
          >
            <Kicker as="h3">Recommendation</Kicker>
            <MarkdownProse
              content={recommendation}
              className="text-sm leading-normal text-foreground"
            />
          </section>
        )}
      </div>

      {/* Footer Actions & Triage */}
      <footer className="mt-6 space-y-4 border-t border-border/60 pt-4">
        {canEdit && (
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
              {isProblem &&
                !["fix_applied", "verified", "dismissed"].includes(
                  effectiveState,
                ) && (
                  <FindingFixDropdown
                    findingId={canonicalOrRawId}
                    projectId={projectId}
                    hasLinkedRepo={hasLinkedRepo}
                    state={effectiveState}
                    size="sm"
                  />
                )}
              <FindingTriageMenu
                findingId={canonicalOrRawId}
                projectId={projectId}
                state={effectiveState as any}
                findingType={finding.type}
                subject={displayTitle.slice(0, 60)}
                size="sm"
                className="flex-1"
              />
            </div>

            {isProblem && (
              <div className="flex flex-wrap items-center gap-2">
                {onCreateIssue && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 text-xs"
                    disabled={createIssuePending || !isTrackerConfigured}
                    onClick={() => onCreateIssue(finding)}
                    title={
                      !isTrackerConfigured
                        ? "Connect issue tracker in Project Settings"
                        : undefined
                    }
                  >
                    {createIssuePending ? (
                      <Loader2 className="mr-1.5 size-3.5 animate-spin" />
                    ) : (
                      <ProviderIcon
                        provider={activeProvider || undefined}
                        className="mr-1.5 size-3.5"
                      />
                    )}
                    Create{" "}
                    {activeProvider
                      ? activeProvider === "linear"
                        ? "Linear"
                        : activeProvider === "jira"
                          ? "Jira"
                          : "GitHub"
                      : ""}{" "}
                    issue…
                  </Button>
                )}

                {finding.canonicalFindingId && onLinkExisting && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 text-xs"
                    onClick={() => onLinkExisting(finding.canonicalFindingId!)}
                  >
                    <Link2 className="mr-1.5 size-3.5" />
                    Link existing…
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between pt-1 text-xs">
          <Link
            to={`/projects/${projectId}/findings/${finding.id}`}
            className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
          >
            <span>Open finding page</span>
            <ExternalLink className="size-3" aria-hidden="true" />
          </Link>
        </div>
      </footer>
    </div>
  );
}
